/**
 * PIIEventsModal - Display events with PII detected
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Tag,
  Button,
  Alert,
  Spin
} from 'antd';
import {
  EyeOutlined
} from '@ant-design/icons';
import { getConnectorPIIEvents, getProjectPIIEvents } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';
import EventDetailModal from './EventDetailModal';

function PIIEventsModal({ visible, onClose, projectId, connectorId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0
  });
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [eventDetailVisible, setEventDetailVisible] = useState(false);

  useEffect(() => {
    if (visible) {
      fetchPIIEvents(1);
    }
  }, [visible, connectorId, projectId]);

  const fetchPIIEvents = async (page = 1) => {
    setLoading(true);
    try {
      let response;
      if (connectorId) {
        response = await getConnectorPIIEvents(connectorId, page, pagination.pageSize);
      } else if (projectId) {
        response = await getProjectPIIEvents(projectId, page, pagination.pageSize);
      }

      if (response?.data) {
        setEvents(response.data.events || []);
        setPagination({
          current: response.data.pagination.page,
          pageSize: response.data.pagination.per_page,
          total: response.data.pagination.total
        });
      }
    } catch (error) {
      console.error('Failed to fetch PII events:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    fetchPIIEvents(pagination.current);
  };

  const columns = [
    {
      title: 'Event ID',
      dataIndex: 'id',
      key: 'id',
      width: 100
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      width: 120,
      render: (provider) => <Tag color="blue">{provider}</Tag>
    },
    {
      title: 'Event Type',
      dataIndex: 'event_type',
      key: 'event_type',
      width: 150,
      render: (type) => <Tag>{type}</Tag>
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      width: 150,
      ellipsis: true
    },
    {
      title: 'PII Types',
      dataIndex: 'pii_types',
      key: 'pii_types',
      width: 200,
      render: (types) => (
        <>
          {types && types.map((type, idx) => (
            <Tag color="red" key={idx}>{type}</Tag>
          ))}
        </>
      )
    },
    {
      title: 'Masked',
      dataIndex: 'masking_enabled',
      key: 'masking_enabled',
      width: 80,
      render: (masked) => (
        <Tag color={masked ? 'green' : 'default'}>
          {masked ? 'Yes' : 'No'}
        </Tag>
      )
    },
    {
      title: 'Storage Tier',
      dataIndex: 'storage_tier',
      key: 'storage_tier',
      width: 140,
      render: (tier) => {
        const tierColors = {
          metadata_only: 'green',
          smart_selective: 'orange',
          full_events: 'red'
        };
        return <Tag color={tierColors[tier] || 'default'}>{tier}</Tag>;
      }
    },
    {
      title: 'Timestamp',
      dataIndex: 'ts',
      key: 'ts',
      width: 180,
      render: (ts) => new Date(ts).toLocaleString()
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedEventId(record.id);
            setEventDetailVisible(true);
          }}
        >
          Details
        </Button>
      )
    }
  ];

  return (
    <>
      <Modal
        title="Events with PII Detected"
        open={visible}
        onCancel={onClose}
        width={1200}
        footer={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>
        ]}
      >
        <Alert
          message="PII Events"
          description={`Showing events where PII was detected. Total: ${pagination.total} events`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Spin spinning={loading}>
          <Table
            dataSource={events}
            columns={columns}
            rowKey="id"
            pagination={{
              ...pagination,
              showSizeChanger: false,
              showTotal: (total) => `Total ${total} events`
            }}
            onChange={handleTableChange}
            scroll={{ x: 1100 }}
            size="small"
          />
        </Spin>
      </Modal>

      {/* Event Detail Modal */}
      {selectedEventId && (
        <EventDetailModal
          visible={eventDetailVisible}
          onClose={() => {
            setEventDetailVisible(false);
            setSelectedEventId(null);
          }}
          eventId={selectedEventId}
        />
      )}
    </>
  );
}

export default PIIEventsModal;
